import React, { useState } from "react";
import api from "@/lib/api";
import { Upload, ArrowRight, CheckCircle2, AlertTriangle } from "lucide-react";

export default function ContactsImport({ onClose, onDone }) {
    const [step, setStep] = useState(1);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [preview, setPreview] = useState(null);
    const [mapping, setMapping] = useState({});
    const [validation, setValidation] = useState(null);
    const [tags, setTags] = useState("");
    const [result, setResult] = useState(null);

    const upload = async (file) => {
        if (!file) return;
        setBusy(true); setError(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/crm/contacts/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setPreview(r.data);
            setMapping(r.data.suggested_mapping);
            setStep(2);
        } catch (e) { setError(e?.response?.data?.detail || "Errore caricamento file"); }
        finally { setBusy(false); }
    };

    const validate = async () => {
        setBusy(true); setError(null);
        try {
            const r = await api.post("/crm/contacts/import/validate", { session_id: preview.session_id, mapping });
            setValidation(r.data);
            setStep(3);
        } catch (e) { setError(e?.response?.data?.detail || "Errore validazione"); }
        finally { setBusy(false); }
    };

    const commit = async () => {
        setBusy(true); setError(null);
        try {
            const r = await api.post("/crm/contacts/import/commit", {
                session_id: preview.session_id,
                tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
            });
            setResult(r.data);
            setStep(4);
        } catch (e) { setError(e?.response?.data?.detail || "Errore import"); }
        finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-[10060] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
            <div data-testid="import-wizard" className="bg-ink-950 border border-white/15 max-w-3xl w-full max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-5">
                    <h4 className="font-serif text-xl text-ink-50 flex items-center gap-2"><Upload size={17} className="text-gold" /> Importa contatti</h4>
                    <button data-testid="import-close" onClick={onClose} className="text-ink-400 hover:text-ink-50">✕</button>
                </div>
                <div className="flex gap-2 mb-6 text-[10px] uppercase tracking-wider">
                    {["Carica file", "Mappa colonne", "Verifica dati", "Fatto"].map((s, i) => (
                        <span key={s} className={`px-2 py-1 border ${step === i + 1 ? "border-gold text-gold" : step > i + 1 ? "border-green-700/40 text-green-300" : "border-white/10 text-ink-500"}`}>{i + 1}. {s}</span>
                    ))}
                </div>
                {error && <div className="mb-4 p-3 border border-red-700/40 bg-red-900/15 text-xs text-red-200">{error}</div>}

                {step === 1 && (
                    <label data-testid="import-dropzone" className="block border-2 border-dashed border-white/20 hover:border-gold/50 p-12 text-center cursor-pointer transition-colors">
                        <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={(e) => upload(e.target.files[0])} />
                        <Upload size={28} className="mx-auto text-ink-500 mb-3" />
                        <div className="text-sm text-ink-100">{busy ? "Analisi del file..." : "Clicca per caricare un file Excel (.xlsx) o CSV"}</div>
                        <div className="text-[11px] text-ink-500 mt-2">Colonne consigliate: Nome, Cognome, Email, WhatsApp/Telefono, Indirizzo immobile, Città, Note, Vuole vendere, Consenso marketing</div>
                    </label>
                )}

                {step === 2 && preview && (
                    <>
                        <p className="text-xs text-ink-400 mb-4">{preview.total_rows} righe trovate in <b className="text-ink-100">{preview.filename}</b>. Associa le colonne del file ai campi del CRM (suggerimento automatico già applicato):</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                            {Object.entries(preview.crm_fields).map(([field, label]) => (
                                <div key={field} className="flex items-center gap-2">
                                    <span className="text-xs text-ink-300 w-44 shrink-0">{label}</span>
                                    <select data-testid={`map-${field}`} value={mapping[field] || ""} onChange={(e) => setMapping((m) => ({ ...m, [field]: e.target.value || null }))}
                                        className="flex-1 bg-ink-950 border border-white/15 px-2 py-1.5 text-xs text-ink-100 outline-none">
                                        <option value="">— non importare —</option>
                                        {preview.headers.map((h) => <option key={h} value={h}>{h}</option>)}
                                    </select>
                                </div>
                            ))}
                        </div>
                        <div className="text-[10px] text-ink-500 mb-4">Anteprima prime righe: {preview.sample_rows.slice(0, 2).map((r, i) => <div key={Object.values(r).join("|").slice(0, 60) || i} className="truncate">{Object.values(r).filter(Boolean).slice(0, 5).join(" · ")}</div>)}</div>
                        <button data-testid="import-validate-btn" onClick={validate} disabled={busy} className="cta-gold px-5 py-2.5 text-xs flex items-center gap-2">{busy ? "Verifica..." : <>Verifica dati <ArrowRight size={12} /></>}</button>
                    </>
                )}

                {step === 3 && validation && (
                    <>
                        <div className="grid grid-cols-3 gap-3 mb-5 text-center">
                            <div className="p-4 border border-green-700/40 bg-green-900/10"><div className="text-2xl text-green-300 font-serif">{validation.valid_count}</div><div className="text-[10px] uppercase text-ink-400">Validi</div></div>
                            <div className="p-4 border border-red-700/40 bg-red-900/10"><div className="text-2xl text-red-300 font-serif">{validation.invalid.length}</div><div className="text-[10px] uppercase text-ink-400">Non validi</div></div>
                            <div className="p-4 border border-amber-700/40 bg-amber-900/10"><div className="text-2xl text-amber-300 font-serif">{validation.duplicates.length}</div><div className="text-[10px] uppercase text-ink-400">Duplicati</div></div>
                        </div>
                        {(validation.invalid.length > 0 || validation.duplicates.length > 0) && (
                            <div data-testid="import-issues" className="mb-5 max-h-40 overflow-y-auto border border-white/10 p-3 space-y-1">
                                {validation.invalid.map((r, i) => <div key={`i${i}`} className="text-[11px] text-red-300 flex items-center gap-2"><AlertTriangle size={10} /> Riga {r.row} · {r.name}: {r.reason}</div>)}
                                {validation.duplicates.map((r, i) => <div key={`d${i}`} className="text-[11px] text-amber-300 flex items-center gap-2"><AlertTriangle size={10} /> Riga {r.row} · {r.name}: {r.reason} (verrà saltata)</div>)}
                            </div>
                        )}
                        <div className="mb-5">
                            <label className="text-xs text-ink-300 block mb-1.5">Tag da assegnare (separati da virgola, opzionale)</label>
                            <input data-testid="import-tags-input" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="es. Import Giugno, Zona Milano Nord"
                                className="w-full bg-transparent border border-white/15 px-3 py-2 text-xs text-ink-50 focus:border-gold outline-none" />
                            <p className="text-[10px] text-ink-500 mt-1.5">Il tag "Proprietario - vuole vendere" viene aggiunto automaticamente ai contatti marcati come interessati a vendere.</p>
                        </div>
                        <button data-testid="import-commit-btn" onClick={commit} disabled={busy || validation.valid_count === 0} className="cta-gold px-5 py-2.5 text-xs disabled:opacity-40">
                            {busy ? "Importazione..." : `Importa ${validation.valid_count} contatti`}
                        </button>
                    </>
                )}

                {step === 4 && result && (
                    <div className="text-center py-8">
                        <CheckCircle2 size={40} className="mx-auto text-green-400 mb-4" />
                        <div data-testid="import-success" className="text-lg text-ink-50 font-serif mb-2">{result.imported} contatti importati con successo</div>
                        <button data-testid="import-done-btn" onClick={onDone} className="cta-gold px-6 py-2.5 text-xs mt-3">Vai alla rubrica</button>
                    </div>
                )}
            </div>
        </div>
    );
}
